import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Departamento, Usuario } from '../../types';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import { useAuth } from '../../contexts/AuthContext';
import { useUsuario } from '../../contexts/UsuarioContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import DetalheDepartamento from './DetalheDepartamento';
import DepartamentoGraficos from './DepartamentoGraficos';
import FiltrosDepartamento, { FiltrosState } from './FiltrosDepartamento';
import api from '../../services/api';

const DepartamentosCards: React.FC = () => {
  const navigate = useNavigate();
  const {
    departamentos,
    carregarDetalhesDepartamento,
    ehLiderDepartamento,
  } = useDepartamento();
  const { usuario } = useAuth();
  const [idsLiderados, setIdsLiderados] = useState<string[]>([]);

  // Busca lideranças diretamente uma única vez quando o usuário é líder
  useEffect(() => {
    if (!usuario || !(usuario.ehLider || usuario.eh_lider)) return;
    api.get(`/usuarios/${usuario.id}/liderancas`)
      .then(res => {
        const ids = (res.data.liderancas || []).map((l: any) => l.id);
        setIdsLiderados(ids);
      })
      .catch(() => {});
  }, [usuario?.id]);
  const { usuarios: todosUsuarios } = useUsuario();
  const [departamentoSelecionado, setDepartamentoSelecionado] = useState<Departamento | null>(null);
  const [mostrarDetalhes, setMostrarDetalhes] = useState(false);
  const [membrosDepartamento, setMembrosDepartamento] = useState<Usuario[]>([]);
  const [modalTarefas, setModalTarefas] = useState<{ departamento: Departamento; statusLabel: string } | null>(null);
  const [tarefasModal, setTarefasModal] = useState<any[]>([]);
  const [carregandoTarefasModal, setCarregandoTarefasModal] = useState(false);
  const [filtros, setFiltros] = useState<FiltrosState>({
    departamentoId: '',
    usuarioId: '',
    status: '',
    prioridade: '',
    dataInicio: '',
    dataFim: '',
  });
  const [modoVisualizacao, setModoVisualizacao] = useState<'cards' | 'graficos'>('graficos');
  const [departamentosComFiltros, setDepartamentosComFiltros] = useState<Departamento[]>([]);
  const [lideresDepartamento, setLideresDepartamento] = useState<Usuario[]>([]);
  
  // Função para filtrar usuários por departamento
  const filtrarUsuariosPorDepartamento = (departamentoId: string): Usuario[] => {
    return todosUsuarios.filter(u => 
      u.departamentoId === departamentoId || u.departamento_id === departamentoId
    );
  };
  
  // Atualizar membros e líderes quando o departamento selecionado mudar
  useEffect(() => {
    if (departamentoSelecionado && todosUsuarios.length > 0) {
      const membros = filtrarUsuariosPorDepartamento(departamentoSelecionado.id);
      setMembrosDepartamento(membros);
    }
    if (departamentoSelecionado) {
      api.get(`/departamentos/${departamentoSelecionado.id}`)
        .then(res => setLideresDepartamento(res.data.departamento?.lideres || []))
        .catch(() => setLideresDepartamento([]));
    }
  }, [departamentoSelecionado, todosUsuarios]);

  // Buscar estatísticas filtradas quando os filtros mudarem
  useEffect(() => {
    let cancelled = false;

    const buscarEstatisticasFiltradas = async () => {
      const temFiltrosAtivos = filtros.usuarioId || filtros.status || filtros.prioridade || filtros.dataInicio || filtros.dataFim;

      if (!temFiltrosAtivos) {
        setDepartamentosComFiltros([]);
        return;
      }

      // Só busca o(s) departamento(s) necessário(s): se filtro de depto estiver ativo, busca apenas ele
      const deptParaBuscar = filtros.departamentoId
        ? departamentos.filter(d => d.id === filtros.departamentoId)
        : departamentos;

      const params = new URLSearchParams();
      if (filtros.usuarioId) params.append('usuarioId', filtros.usuarioId);
      if (filtros.status) params.append('status', filtros.status);
      if (filtros.prioridade) params.append('prioridade', filtros.prioridade);
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim);

      try {
        const departamentosAtualizados = await Promise.all(
          deptParaBuscar.map(async (dept) => {
            try {
              const response = await api.get(`/departamentos/estatisticas/${dept.id}?${params.toString()}`);
              const stats = response.data.estatisticas;
              return {
                ...dept,
                tarefas_pendentes: parseInt(stats.tarefas_pendentes) || 0,
                tarefas_em_andamento: parseInt(stats.tarefas_em_andamento) || 0,
                tarefas_aguardando_aprovacao: parseInt(stats.tarefas_aguardando_aprovacao) || 0,
                tarefas_ajustes_requisitados: parseInt(stats.tarefas_ajustes_requisitados) || 0,
                tarefas_concluidas: parseInt(stats.tarefas_concluidas) || 0,
                tarefas_ativas: parseInt(stats.tarefas_ativas) || 0,
                tarefas: {
                  pendentes: parseInt(stats.tarefas_pendentes) || 0,
                  aIniciar: parseInt(stats.tarefas_a_iniciar) || 0,
                  emAndamento: parseInt(stats.tarefas_em_andamento) || 0,
                  aguardando: parseInt(stats.tarefas_aguardando_aprovacao) || 0,
                  ajustes: parseInt(stats.tarefas_ajustes_requisitados) || 0,
                  concluidas: parseInt(stats.tarefas_concluidas) || 0,
                  concluidasComAtraso: parseInt(stats.tarefas_concluidas_com_atraso) || 0,
                  emAndamentoComAtraso: parseInt(stats.tarefas_em_andamento_com_atraso) || 0,
                  emAndamentoNoPrazo: stats.tarefas_em_andamento_no_prazo != null
                    ? parseInt(stats.tarefas_em_andamento_no_prazo) || 0
                    : undefined,
                  arquivadas: parseInt(stats.tarefas_arquivadas) || 0,
                  totalAtivas: parseInt(stats.tarefas_ativas) || 0,
                  ativasNaoArquivadas: parseInt(stats.tarefas_ativas) || 0
                }
              };
            } catch {
              return dept;
            }
          })
        );

        if (!cancelled) setDepartamentosComFiltros(departamentosAtualizados);
      } catch (error) {
        if (!cancelled) setDepartamentosComFiltros([]);
      }
    };

    buscarEstatisticasFiltradas();
    return () => { cancelled = true; };
  }, [filtros, departamentos]);

  if (!usuario) {
    return null;
  }

  // Usar departamentos com filtros aplicados se houver filtros ativos, senão usar departamentos originais
  const temFiltrosAtivos = filtros.usuarioId || filtros.status || filtros.prioridade || filtros.dataInicio || filtros.dataFim;
  const departamentosParaUsar = (temFiltrosAtivos && departamentosComFiltros.length > 0 ? departamentosComFiltros : departamentos)
    .filter(d => d.nome !== 'Compras' && d.nome !== 'Gerenciamento de jogadores');

  // Filtrar departamentos com base no tipo de usuário e filtros aplicados
  const ehLider = usuario.ehLider || usuario.eh_lider || ehLiderDepartamento;
  let departamentosFiltrados = usuario.tipo === 'admin'
    ? departamentosParaUsar // Admin vê todos
    : ehLider
      ? departamentosParaUsar.filter(dep =>
          // Líder vê TODOS os departamentos que lidera — usa idsLiderados quando disponível
          idsLiderados && idsLiderados.length > 0
            ? idsLiderados.includes(dep.id)
            : dep.id === (usuario.departamentoId || usuario.departamento_id)
        )
      : departamentosParaUsar.filter(dep => dep.id === (usuario.departamentoId || usuario.departamento_id));

  // Aplicar filtro de departamento se selecionado
  if (filtros.departamentoId) {
    departamentosFiltrados = departamentosFiltrados.filter(dep => dep.id === filtros.departamentoId);
  }

  // Se não houver departamentos para exibir, não renderiza o componente
  if (departamentosFiltrados.length === 0) {
    return null;
  }

  const STATUS_LABELS: Record<string, string> = {
    'Pendente': 'Pendente',
    'A iniciar': 'A iniciar',
    'Em Andamento': 'Em Andamento',
    'Concluída': 'Concluída',
    'concluida_no_prazo': 'Concluídas no Prazo',
    'concluida_com_atraso': 'Concluídas com Atraso',
    'em_andamento_no_prazo': 'Em Andamento (no prazo)',
    'em_andamento_com_atraso': 'Em Andamento com Atraso',
    'Arquivada': 'Arquivada',
  };

  const abrirModalTarefas = async (departamento: Departamento, statusKey: string) => {
    const statusLabel = STATUS_LABELS[statusKey] || statusKey;
    setModalTarefas({ departamento, statusLabel });
    setCarregandoTarefasModal(true);
    setTarefasModal([]);
    try {
      const params: Record<string, any> = { departamento: departamento.id, limite: 200, incluirConcluidas: true };
      if (statusKey === 'Arquivada') {
        params.arquivadas = true;
      } else if (statusKey === 'em_andamento_com_atraso') {
        // Inclui arquivadas — não envia o filtro arquivadas
        params.status = statusKey;
      } else if (
        statusKey === 'concluida_com_atraso' ||
        statusKey === 'concluida_no_prazo' ||
        statusKey === 'em_andamento_no_prazo'
      ) {
        params.status = statusKey;
        params.arquivadas = false;
      } else {
        params.status = statusKey;
        params.arquivadas = false;
      }
      const response = await api.get('/tarefas/buscar', { params });
      setTarefasModal(response.data.tarefas || []);
    } catch (error) {
      console.error('Erro ao buscar tarefas do modal:', error);
    } finally {
      setCarregandoTarefasModal(false);
    }
  };

  // Mapeia o nome da fatia/legenda do gráfico para o status usado na busca
  const SLICE_TO_STATUS: Record<string, string> = {
    'Pendentes': 'Pendente',
    'A iniciar': 'A iniciar',
    'Em Andamento': 'em_andamento_no_prazo',
    'Em And. c/ Atraso': 'em_andamento_com_atraso',
    'Concluídas': 'Concluída',
    'Concluídas no Prazo': 'concluida_no_prazo',
    'Concluídas c/ Atraso': 'concluida_com_atraso',
    'Arquivadas': 'Arquivada',
  };

  const handleStatusClick = (departamento: Departamento, sliceName: string) => {
    const statusKey = SLICE_TO_STATUS[sliceName];
    if (statusKey) {
      abrirModalTarefas(departamento, statusKey);
    }
  };

  const handleVerDetalhes = async (departamento: Departamento) => {
    if (filtros.status) {
      abrirModalTarefas(departamento, filtros.status);
      return;
    }

    try {
      await carregarDetalhesDepartamento(departamento.id);
      const membros = filtrarUsuariosPorDepartamento(departamento.id);
      setMembrosDepartamento(membros);
      setDepartamentoSelecionado(departamento);
      setMostrarDetalhes(true);
    } catch (error) {
      console.error('Erro ao carregar detalhes do departamento:', error);
    }
  };

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          {usuario.tipo === 'admin'
            ? 'Todos os Departamentos'
            : (idsLiderados && idsLiderados.length > 1)
              ? 'Meus Departamentos'
              : ehLider
                ? 'Meu Departamento'
                : 'Meu Departamento'}
        </h2>
        
        {/* Toggle de visualização */}
        <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setModoVisualizacao('graficos')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              modoVisualizacao === 'graficos'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Gráficos
          </button>
          <button
            onClick={() => setModoVisualizacao('cards')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              modoVisualizacao === 'cards'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            Cards
          </button>
        </div>
      </div>

      {/* Filtros avançados — Admin vê tudo; Líder vê apenas seu departamento */}
      {(usuario.tipo === 'admin' || usuario.ehLider || usuario.eh_lider) && (() => {
        const isLider = usuario.tipo !== 'admin' && (usuario.ehLider || usuario.eh_lider);
        const usuariosParaFiltro = isLider
          ? todosUsuarios.filter(u => {
              const depId = u.departamentoId || u.departamento_id;
              // Inclui usuários de TODOS os departamentos que o líder gerencia
              return idsLiderados.length > 0
                ? !!depId && idsLiderados.includes(depId)
                : depId === (usuario.departamentoId || usuario.departamento_id);
            })
          : todosUsuarios;

        return (
          <FiltrosDepartamento
            departamentos={departamentos}
            usuarios={usuariosParaFiltro}
            onFiltroChange={setFiltros}
            ocultarFiltroDepto={isLider}
          />
        );
      })()}

      {/* Renderização condicional baseada no modo de visualização */}
      {modoVisualizacao === 'graficos' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {departamentosFiltrados.map((departamento) => (
            <div key={departamento.id} onClick={() => handleVerDetalhes(departamento)} className="cursor-pointer">
              <DepartamentoGraficos
                departamento={departamento}
                onStatusClick={(sliceName) => handleStatusClick(departamento, sliceName)}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {departamentosFiltrados.map((departamento) => (
          <div 
            key={departamento.id} 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer"
            onClick={() => handleVerDetalhes(departamento)}
          >
            <div className="flex justify-between items-start mb-3">
              <h3 className="text-lg font-semibold text-gray-800">{departamento.nome}</h3>
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                {departamento.tarefas
                  ? (departamento.tarefas.pendentes || 0) + (departamento.tarefas.aIniciar || 0) +
                    (departamento.tarefas.emAndamento || 0) + (departamento.tarefas.concluidas || 0) +
                    (departamento.tarefas.arquivadas || 0)
                  : departamento.tarefas_ativas || 0} tarefas
              </span>
            </div>
            
            <p className="text-gray-600 text-sm mb-4 line-clamp-2">{departamento.descricao}</p>
            
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="bg-gray-50 dark:bg-gray-900/40 p-2 rounded">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">Pendentes</span>
                <span className="font-medium text-yellow-600 dark:text-yellow-400">{departamento.tarefas?.pendentes || departamento.tarefas_pendentes || 0}</span>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/40 p-2 rounded">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">A iniciar</span>
                <span className="font-medium text-blue-600 dark:text-blue-400">{departamento.tarefas?.aIniciar || 0}</span>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/40 p-2 rounded">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">Em Andamento</span>
                <span className="font-medium text-purple-600 dark:text-purple-400">{departamento.tarefas?.emAndamento || departamento.tarefas_em_andamento || 0}</span>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/40 p-2 rounded">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">Em And. c/ Atraso</span>
                <span className="font-medium text-amber-600 dark:text-amber-400">{departamento.tarefas?.emAndamentoComAtraso || 0}</span>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/40 p-2 rounded">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">Aguardando</span>
                <span className="font-medium text-orange-600 dark:text-orange-400">{departamento.tarefas?.aguardando || departamento.tarefas_aguardando_aprovacao || 0}</span>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/40 p-2 rounded">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">Concluídas</span>
                <span className="font-medium text-green-600 dark:text-green-400">{departamento.tarefas?.concluidas || departamento.tarefas_concluidas || 0}</span>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/40 p-2 rounded">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">Concl. c/ Atraso</span>
                <span className="font-medium text-red-600 dark:text-red-400">{departamento.tarefas?.concluidasComAtraso || 0}</span>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/40 p-2 rounded">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">Arquivadas</span>
                <span className="font-medium text-gray-700 dark:text-gray-300">{departamento.tarefas?.arquivadas || 0}</span>
              </div>
            </div>
            
            <div className="text-xs text-gray-500 mt-2">
              Última atualização: {departamento.ultima_atualizacao ? 
                format(new Date(departamento.ultima_atualizacao), "dd 'de' MMMM, yyyy HH:mm", { locale: ptBR }) :
                'Não disponível'}
            </div>
          </div>
          ))}
        </div>
      )}

      {/* Modal de tarefas filtradas por status */}
      {modalTarefas && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center p-5 border-b">
              <div>
                <h2 className="text-xl font-bold text-gray-800">{modalTarefas.departamento.nome}</h2>
                <span className="text-sm text-gray-500">Status: <span className="font-medium text-blue-600">{modalTarefas.statusLabel}</span></span>
              </div>
              <button onClick={() => setModalTarefas(null)} className="text-gray-400 hover:text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-4">
              {carregandoTarefasModal ? (
                <div className="flex justify-center items-center h-40">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : tarefasModal.length === 0 ? (
                <p className="text-center text-gray-500 py-10">Nenhuma tarefa encontrada.</p>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-500 mb-3">{tarefasModal.length} tarefa{tarefasModal.length !== 1 ? 's' : ''} encontrada{tarefasModal.length !== 1 ? 's' : ''}</p>
                  {tarefasModal.map((t: any) => (
                    <div key={t.id} onClick={() => { setModalTarefas(null); navigate(`/tarefas/${t.id}`); }} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-400 mb-1">#{t.id?.slice(-6)}</p>
                          <p className="font-medium text-gray-800 truncate">{t.titulo}</p>
                          {t.responsavel_nome && (
                            <p className="text-sm text-gray-500 mt-1">Responsável: {t.responsavel_nome}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            t.status === 'Concluída' || t.status === 'Finalizada' ? 'bg-green-100 text-green-700' :
                            t.status === 'Em Andamento' || t.status === 'Em Execução' ? 'bg-purple-100 text-purple-700' :
                            t.status === 'Pendente' || t.status === 'Solicitado' ? 'bg-yellow-100 text-yellow-700' :
                            t.status === 'A iniciar' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>{t.status}</span>
                          {t.prioridade && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              t.prioridade === 'urgente' ? 'bg-red-100 text-red-700' :
                              t.prioridade === 'alta' ? 'bg-orange-100 text-orange-700' :
                              t.prioridade === 'media' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>{t.prioridade}</span>
                          )}
                          {t.data_vencimento && (
                            <p className="text-xs text-gray-400">{format(new Date(t.data_vencimento), 'dd/MM/yyyy', { locale: ptBR })}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalhes do departamento */}
      {mostrarDetalhes && departamentoSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-xl font-bold">Detalhes do Departamento</h2>
              <button 
                onClick={() => setMostrarDetalhes(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <DetalheDepartamento 
                departamento={departamentoSelecionado}
                lideres={lideresDepartamento}
                membros={membrosDepartamento}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartamentosCards;
